/* Single entry point for the Scale runtime.
 *
 * Element registration is NOT done here. The React proxies in
 * @telekom/scale-components-react are built with the Stencil react-output-target,
 * and each proxy carries its own defineCustomElement — importing a proxy registers
 * exactly the element it needs. Calling the loader's defineCustomElements(window)
 * on top of that would eagerly register the whole library and re-define elements
 * the proxies had already claimed.
 *
 * What does need doing once, at boot, is the stylesheet and the colour scheme.
 * Scale keys its own dark palette off data-mode on the root element, so that one
 * attribute drives both the Scale components and the cockpit's own tokens. */

import '@telekom/scale-components/dist/scale-components/scale-components.css';
import '../styles/cloudorder.css';

export type ColorScheme = 'light' | 'dark';

const STORAGE_KEY = 'cloudorder.color-scheme';
const MODE_ATTRIBUTE = 'data-mode';

let initialized = false;

function isColorScheme(value: string | null): value is ColorScheme {
  return value === 'light' || value === 'dark';
}

function readStoredScheme(): ColorScheme | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isColorScheme(stored) ? stored : null;
  } catch {
    // Private browsing modes throw on localStorage access. A missing preference
    // is not an error; fall through to the system setting.
    return null;
  }
}

function systemScheme(): ColorScheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** The scheme currently applied to the document. */
export function getColorScheme(): ColorScheme {
  const attr = document.documentElement.getAttribute(MODE_ATTRIBUTE);
  return isColorScheme(attr) ? attr : 'light';
}

/** Apply a scheme and remember it, so the choice survives a reload. */
export function setColorScheme(scheme: ColorScheme): void {
  document.documentElement.setAttribute(MODE_ATTRIBUTE, scheme);
  try {
    window.localStorage.setItem(STORAGE_KEY, scheme);
  } catch {
    // The scheme is applied either way; only persistence is lost.
  }
}

/**
 * Call once, before the first render. Repeat calls are ignored so a hot reload
 * or a stray import cannot re-run the boot work.
 */
export function initializeScale(options: { colorScheme?: ColorScheme } = {}): void {
  if (initialized) return;
  initialized = true;
  if (typeof document === 'undefined') return;

  const scheme = options.colorScheme ?? readStoredScheme() ?? systemScheme();
  document.documentElement.setAttribute(MODE_ATTRIBUTE, scheme);
}
